import React, { useMemo } from 'react';
import { C } from '../../utils/styles';
import { useConfig } from '../../context/ConfigContext';
import { optimizeEmployeeWithEligibility } from '../../utils/calculations';
import EnrollmentProgressSection from './EnrollmentProgressSection';
import Section125SummarySection from './Section125SummarySection';
import IncreaseSavingsSection from './IncreaseSavingsSection';
import CompleteYourSetupSection from './CompleteYourSetupSection';
import MessagesSection from './MessagesSection';
import YourHealthCuesTeamSection from './YourHealthCuesTeamSection';
import Section105PortalCard from './Section105PortalCard';
import SavedOptionsComparisonView from './SavedOptionsComparisonView';

/**
 * The employer dashboard — 6 sections top to bottom.
 * Props:
 *   client: full client record
 *   currentUser: logged-in user (employer contact)
 *   onOpenChecklist(stepId): opens the onboarding checklist modal
 */
export default function EmployerDashboard({ client, currentUser, onOpenChecklist }) {
  const { config } = useConfig();

  // Analyze census employees so the IncreaseSavings section can run EligibilityAdvisor.
  // Keep this lean: analyze only if we have both employees and config.
  const analyzed = useMemo(() => {
    const employees = client?.employees || [];
    if (!employees.length || !config) return [];
    try {
      return employees.map(e => {
        // Re-parse salary in case it was stored as string
        const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
        if (!salaryNum) return e;
        return optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
      });
    } catch (err) {
      console.warn('Portal analyze failed, continuing without advisor:', err);
      return [];
    }
  }, [client?.employees, config]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Top row — compact Enrollment Progress (left) + Section 125 Summary (right).
          Use `alignItems: stretch` (default) and let the children be `height:100%` so
          both cards always share the same height regardless of which one has more
          content. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 380px) 1fr',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        <EnrollmentProgressSection client={client} />
        <Section125SummarySection client={client} />
      </div>
      <Section105PortalCard client={client} />
      <SavedOptionsComparisonView client={client} />
      <CompleteYourSetupSection client={client} onOpenChecklist={onOpenChecklist} />
      <MessagesSection
        clientId={client?.id || client?._id}
        currentUserId={currentUser?.id || currentUser?._id}
        variant="portal"
      />
      <IncreaseSavingsSection client={client} analyzed={analyzed} config={config} />
      <YourHealthCuesTeamSection client={client} />
    </div>
  );
}
